import { AdequacyAggregateType, ExportFormatType, ExportGapFilterType, ExportProviderAvailabilityType, ExportType, ReportFormatType, NetworkProviderFilterType } from "@app/exports/enumerations";
import { toast } from "react-toastify";
import ExportService from "@app/exports/exportService";
import { TimeFrequency } from "@app/projects/enumerators";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import Features from "@app/feature-flags/features";
import { ExportAdequacyServicingProvidersModel } from "@app/exports/exportModels";
import { ExportColumn } from "@app/report-customization/build-reports/interfaces/exportColumns";
import { ExportModels } from "@app/report-customization/build-reports/interfaces/exportModels";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import Roles from "@app/user/roles";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";

const exportService = new ExportService();

interface FormatsReturnType {
  key: string;
  value: string;
}

export const intersectSpecialties = (primarySpecialties: DropdownItem<string>[], secondarySpecialties: DropdownItem<string>[] = []): DropdownItem<string>[] => {
  return (secondarySpecialties.length > 0
    ? primarySpecialties.filter((primary) => !!secondarySpecialties.find((secondary) => primary.key === secondary.key))
    : [...primarySpecialties])
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

export const getDefaultReportName = (prefix: string): string => {
  // this returns an array formatted like ['02', '03', '2025, 10:48:54 AM']
  const date = (new Date()).toLocaleDateString("en-US", { day: "2-digit", hour: "2-digit", minute: "2-digit", month: "2-digit", second: "2-digit", year: "numeric" }).split("/");
  // ['2025', '10:48:54 AM']
  const yearAndTime = date[2].split(", ");
  // ['10', '48', '54']
  const time = yearAndTime[1].split(" ")[0].split(":");
  // 20250203_104854
  const dateString = `${yearAndTime[0]}${date[0]}${date[1]}_${time[0]}${time[1]}${time[2]}`;

  return `${prefix}_${dateString}`;
}

export const getDefaultReportNamePrefixByReportType = (reportType: ReportType, isCompareMode?: boolean, providerSource?: ProviderNetworkTypeEnum): string => {
  switch (reportType) {
    case ReportType.AdequacyServicingProvider:
    case ReportType.ZipAnalysisAdequacyServicingProvider:
      if (isCompareMode)
        return "Changed Providers";

      switch (providerSource) {
        case ProviderNetworkTypeEnum.Market:
          return "Market";
        case ProviderNetworkTypeEnum.Prospect:
          return "Prospect";
        case ProviderNetworkTypeEnum.NetNew:
          return "Net New";
        case ProviderNetworkTypeEnum.NextClosestProviders:
          return "Next Closest Provider";
        case ProviderNetworkTypeEnum.Network:
        default:
          return "Network";
      }
    case ReportType.ZipAnalysisResultsReport:
      return isCompareMode ? "Summary by Zip Code Comparison" : "Summary by Zip Code";
    case ReportType.ZipClassAggregateResultsReport:
      return isCompareMode ? "Summary by Zip Code Classification Comparison" : "Summary by Zip Code Classification";
    case ReportType.StateSummary:
      return isCompareMode ? "State Comparison" : "State";
    case ReportType.RegionSummary:
      return isCompareMode ? "Region Comparison" : "Region";
    case ReportType.CountySummary:
      return isCompareMode ? "County Comparison" : "County";
    case ReportType.AdequacySpecialtySummary:
      return isCompareMode ? "Specialty Comparison" : "Specialty";
    case ReportType.AdequacyAggregate:
      return isCompareMode ? "Specialties Comparison" : "Specialties";
    case ReportType.SpecialtySummaryByCounty:
      return isCompareMode ? "Summary Comparison" : "Summary";
    case ReportType.ClosestProvidersToMembers:
      // no compare mode option
      return "Closest Providers to Members";
    case ReportType.ZipSummary:
      return isCompareMode ? "Zip Summaries Comparison" : "Zip Summaries";
  }
}

export const ReportTypeNamesMap = new Map<ReportType, string>([
  [ReportType.AdequacySpecialtySummary, "Specialty Summary"],
  [ReportType.AdequacySpecialtySummaryComparison, "Specialty Summmary Comparison"],
  [ReportType.RegionSummary, "Region Summary"],
  [ReportType.RegionSummaryComparison, "Region Summary Comparison"],
  [ReportType.CountySummary, "County Summary"],
  [ReportType.CountySummaryComparison, "County Summary Comparison"],
  [ReportType.StateSummary, "State Summary"],
  [ReportType.StateSummaryComparison, "State Summary Comparison"],
  [ReportType.AdequacyAggregate, "All Specialties"],
  [ReportType.AdequacyAggregateComparison, "All Specialties Comparison"],
  [ReportType.SpecialtySummaryByCounty, "Specialty Summary By County"],
  [ReportType.SpecialtySummaryByCountyComparison, "Specialty Summary By County Comparison"],
  [ReportType.AdequacyServicingProvider, "Providers"],
  [ReportType.AdequacyServicingProviderComparison, "Providers Comparison"],
  [ReportType.ZipAnalysisAdequacyServicingProvider, "Providers by Zip Code"],
  [ReportType.ZipAnalysisResultsReport, "Specialty Summary by Zip Code"],
  [ReportType.SpecialtySummaryByZipBulk, "Specialty Summary by Zip Code Volume"],
  [ReportType.ZipAnalysisResultsReportComparison, "Specialty Summary by Zip Code Comparison"],
  [ReportType.ZipAnalysisAdequacyServicingProviderComparison, "Providers by Zip Code Comparison"],
  [ReportType.ZipClassAggregateResultsReport, "Summary by Zip Code Classification"],
  [ReportType.ZipClassAggregateResultsReportComparison, "Summary by Zip Code Classification"],
  [ReportType.ZipSummary, "Zip Summaries"],
  [ReportType.ZipSummaryComparison, "Zip Summary Comparison"],
  [ReportType.ClosestProvidersToMembers, "Closest Provider to Members"],
  [ReportType.AdequacyBulkTerminationChangedGaps, "Terminated Network Changed Gaps"],
  [ReportType.AdequacyBulkTerminationDistinctProviders, "Terminated Network Distinct Providers"],
  [ReportType.AdequacyBulkTerminationProviders, "Terminated Network Providers"],
  [ReportType.AdequacyBulkTerminationSpecialtySummaryComparison, "Terminated Specialty Summary Comparison"],
  [ReportType.AdequacyBulkTerminationVolumeTermProjectDetails, "Volume Term Project Details"],
  [ReportType.ZipAdequacyBulkTerminationChangedGaps, "Terminated Network Changed Gaps by Zip Code"],
  [ReportType.ZipAdequacyBulkTerminationSpecialtySummaryComparison, "Terminated Specialty Summary Comparison by Zip Code"],
  [ReportType.AdequacyBulkTargetSpecialtySummaryComparison, "Target Specialty Summary Comparison"],
  [ReportType.AdequacyBulkTargetChangedGaps, "Target Changed Gaps"],
  [ReportType.AdequacyBulkTargetProviders, "Target Providers"],
  [ReportType.AdequacyBulkTargetDistinctProviders, "Target Distinct Providers"],
  [ReportType.AdequacyBulkVolumeTargetProjectDetails, "Project Details Target Overview"],
  [ReportType.ZipAdequacyBulkTargetChangedGaps, "Target Changed Gaps by Zip Code"],
  [ReportType.ZipAdequacyBulkTargetSpecialtySummaryComparison, "Target Specialty Summary Comparison by Zip Code"],
  [ReportType.ZipAnalysisServicingProviderBulk, "Providers by Zip Code"],
]);

export const TimeFrequencyNamesMap = new Map<TimeFrequency, string>([
  [TimeFrequency.All, "Refreshes"],
  [TimeFrequency.Monthly, "Monthly Trends"],
]);

export const sharedUsersAggregateTypeMap = new Map<ReportType, AdequacyAggregateType>([
  [ReportType.AdequacyServicingProvider, undefined],
  [ReportType.AdequacyServicingProviderComparison, undefined],
  [ReportType.RegionSummary, AdequacyAggregateType.RegionSet],
  [ReportType.RegionSummaryComparison, AdequacyAggregateType.RegionSet],
  [ReportType.CountySummary, AdequacyAggregateType.RegionSet],
  [ReportType.CountySummaryComparison, AdequacyAggregateType.RegionSet],
  [ReportType.StateSummary, AdequacyAggregateType.RegionSet],
  [ReportType.StateSummaryComparison, AdequacyAggregateType.RegionSet],
  [ReportType.AdequacySpecialtySummary, AdequacyAggregateType.SpecialtyGroup],
  [ReportType.AdequacySpecialtySummaryComparison, AdequacyAggregateType.SpecialtyGroup],
  [ReportType.ClosestProvidersToMembers, undefined],
  [ReportType.ZipSummary, undefined],
  [ReportType.ZipSummaryComparison, undefined],
  [ReportType.AdequacyAggregate, undefined],
  [ReportType.AdequacyAggregateComparison, undefined],
  [ReportType.SpecialtySummaryByCounty, undefined],
  [ReportType.SpecialtySummaryByCountyComparison, undefined],
  [ReportType.ZipAnalysisAdequacyServicingProvider, undefined],
  [ReportType.ZipAnalysisResultsReport, undefined],
  [ReportType.SpecialtySummaryByZipBulk, undefined],
  [ReportType.ZipClassAggregateResultsReport, undefined],
  [ReportType.ZipClassAggregateResultsReportBulk, undefined],
  [ReportType.ZipAnalysisServicingProviderBulk, undefined],
]);

export const exportTypeToSharedUsersExportTypeMap = new Map<ReportType, string>([
  [ReportType.AdequacyAggregate, "AdequacyAggregate"],
  [ReportType.AdequacyAggregateComparison, "AdequacyAggregate"],
  [ReportType.ClosestProvidersToMembers, "ClosestProvidersToMembers"],
  [ReportType.RegionSummary, "AdequacyBulkAggregateReport"],
  [ReportType.RegionSummaryComparison, "AdequacyBulkAggregateReport"],
  [ReportType.CountySummary, "AdequacyBulkAggregateReport"],
  [ReportType.CountySummaryComparison, "AdequacyBulkAggregateReport"],
  [ReportType.AdequacyServicingProvider, "AdequacyServicingProvider"],
  [ReportType.AdequacyServicingProviderComparison, "AdequacyServicingProvider"],
  [ReportType.AdequacySpecialtySummary, "AdequacyAggregate"],
  [ReportType.AdequacySpecialtySummaryComparison, "AdequacyAggregate"],
  [ReportType.SpecialtySummaryByCounty, "AdequacySummary"],
  [ReportType.SpecialtySummaryByCountyComparison, "AdequacySummary"],
  [ReportType.StateSummary, "AdequacyBulkAggregateReport"],
  [ReportType.StateSummaryComparison, "AdequacyBulkAggregateReport"],
  [ReportType.ZipSummary, "ZipSummary"],
  [ReportType.ZipSummaryComparison, "ZipSummary"],
  [ReportType.ZipAnalysisAdequacyServicingProvider, "ZipAnalysisAdequacyServicingProvider"],
  [ReportType.ZipAnalysisResultsReport, "ZipAnalysisResultsReport"],
  [ReportType.SpecialtySummaryByZipBulk, "SpecialtySummaryByZipBulk"],
  [ReportType.ZipClassAggregateResultsReport, "ZipClassAggregateResultsReport"],
  [ReportType.ZipClassAggregateResultsReportBulk, "ZipClassAggregateResultsReportBulk"],
  [ReportType.ZipAnalysisServicingProviderBulk, "ZipAnalysisServicingProviderBulk"],
]);

export const getFileFormats = (tooLarge?: boolean, reportType?: ReportType): FormatsReturnType[] => {
  const fileFormats: Array<{ key: ExportFormatType, value: string }> = [
    { key: ExportFormatType.CSV, value: "Comma-Delimited Text File (.csv)" },
    { key: ExportFormatType.Tab, value: "Tab-Delimited Text File (.txt)" },
    { key: ExportFormatType.Pipe, value: "Bar-Delimited Text File (.txt)" },
  ];

  if (!tooLarge && reportType !== ReportType.ClosestProvidersToMembers && reportType !== ReportType.ZipSummary) {
    fileFormats.push({ key: ExportFormatType.Excel, value: "Microsoft Excel Workbook (.xlsx)" });
  }

  return fileFormats;
}

export const getDefaultFileFormat = (tooLarge?: boolean, reportType?: ReportType): ExportFormatType => {
  const reportTypesThatDoNotDefaultToExcel = [ReportType.ClosestProvidersToMembers, ReportType.ZipSummary, ReportType.AdequacyServicingProvider, ReportType.ZipAnalysisAdequacyServicingProvider, ReportType.ZipAnalysisAdequacyServicingProviderComparison];
  if (!tooLarge && !reportTypesThatDoNotDefaultToExcel.includes(reportType)) {
    return ExportFormatType.Excel;
  }

  return ExportFormatType.CSV;
}

export const getProvidersReportFormats = (userRoles: string[], featureFlags: Set<string>, providerSource: ProviderNetworkTypeEnum): FormatsReturnType[] => {
  const hsdFormatFilters = [
    { key: ReportFormatType.Default, value: "Default Format" },
    { key: ReportFormatType.MA, value: "MA HSD Format" },
  ];

  if (featureFlags.has(Features.SHOW_MMP_HSD_FORMAT)) {
    hsdFormatFilters.push({ key: ReportFormatType.MMP, value: "MMP HSD Format" });
  }

  if (userRoles.includes(Roles.CCIIO_PPF_REPORT_USER)) {
    hsdFormatFilters.push({ key: ReportFormatType.CCIIO_PPF_REPORT, value: "CCIIO PPF Report" });
  }

  if (userRoles.includes(Roles.DISTINCT_SERVICING_USER)
    && featureFlags.has(Features.DISTINCT_SERVICING_PROVIDER_REPORT)
    && (providerSource === ProviderNetworkTypeEnum.Network
      || providerSource === ProviderNetworkTypeEnum.Prospect
      || providerSource === ProviderNetworkTypeEnum.Market
    )) {
    hsdFormatFilters.push({ key: ReportFormatType.DISTINCT_SERVICING_PROVIDER_REPORT, value: "Distinct Servicing Providers" })
  }

  return hsdFormatFilters;
}

export const isPciFilterReportFormat = (reportFormat: ReportFormatType): boolean =>
  reportFormat === ReportFormatType.Default
  || reportFormat === ReportFormatType.CCIIO_PPF_REPORT
  || reportFormat === ReportFormatType.DISTINCT_SERVICING_PROVIDER_REPORT;

type PciFilterExportFields = Pick<ExportAdequacyServicingProvidersModel,
  "selectedCareJourneyCostValues" | "selectedCareJourneyQualityValues" | "selectedEffectivenessValues"
  | "selectedEfficiencyValues" | "selectedLineOfBusiness" | "selectedLocationConfidenceValues"
  | "selectedMotiveAppropriatenessValues" | "selectedNewPatientClaimsValues" | "selectedVolumeLabels">;

export const getPciFilterExportFields = (showPciData: boolean, reportFormat: ReportFormatType, state: PciFilterExportFields): Partial<PciFilterExportFields> =>
  showPciData && isPciFilterReportFormat(reportFormat)
    ? {
      selectedCareJourneyCostValues: state.selectedCareJourneyCostValues,
      selectedCareJourneyQualityValues: state.selectedCareJourneyQualityValues,
      selectedEffectivenessValues: state.selectedEffectivenessValues,
      selectedEfficiencyValues: state.selectedEfficiencyValues,
      selectedLineOfBusiness: state.selectedLineOfBusiness,
      selectedLocationConfidenceValues: state.selectedLocationConfidenceValues,
      selectedMotiveAppropriatenessValues: state.selectedMotiveAppropriatenessValues,
      selectedNewPatientClaimsValues: state.selectedNewPatientClaimsValues,
      selectedVolumeLabels: state.selectedVolumeLabels,
    }
    : {};

export const shouldShowPciFiltersSection = (showPciData: boolean, reportFormat: ReportFormatType): boolean =>
  showPciData && isPciFilterReportFormat(reportFormat);

export const getServiceAreaGapTypes = (userRoles: string[], isZipAnalysisReport?: boolean): FormatsReturnType[] => {
  const filters = [
    { key: ExportGapFilterType.All, value: `All Areas` },
    { key: ExportGapFilterType.Gaps, value: `Areas with Gaps` },
  ];

  if (!isZipAnalysisReport) {
    filters.push({ key: ExportGapFilterType.FillableBySingleProvider, value: `Areas with Gaps Fillable by Single Provider` },);
  }

  filters.push({ key: ExportGapFilterType.Exceptions, value: `Areas with Exceptions` },);

  if (userRoles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER) && !isZipAnalysisReport) {
    filters.push(
      { key: ExportGapFilterType.FillableGaps, value: `Areas with Fillable Gaps` }
    )
  }

  return filters;
}

export const getSpecialtyGapTypes = (userRoles: string[], isZipAnalysisReport?: boolean, isZipGapResolutionStatusEnabled?: boolean): FormatsReturnType[] => {
  const filters = [
    { key: ExportGapFilterType.All, value: "All Specialties" },
    { key: ExportGapFilterType.Gaps, value: "Specialties with Gaps" },
  ];

  if (!isZipAnalysisReport) {
    filters.push({ key: ExportGapFilterType.FillableBySingleProvider, value: "Specialties with Gaps Fillable by Single Provider" },);
  }

  filters.push({ key: ExportGapFilterType.Exceptions, value: "Specialties with Exceptions" },);

  if (userRoles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER) && !isZipAnalysisReport) {
    filters.push(
      { key: ExportGapFilterType.FillableGaps, value: "Specialties with Fillable Gaps" }
    )
  }

  if (!isZipAnalysisReport || isZipGapResolutionStatusEnabled) {
    filters.push(
      { key: ExportGapFilterType.GapResolutionStatus, value: "Specialties with Gap Resolution Status" }
    );
  }

  return filters;
}

export const getProviderFilterOptions = (reportType: ReportType, providerNetworkType: ProviderNetworkTypeEnum, enabledFeatureFlags: Set<string>): FormatsReturnType[] => {
  if (!providerNetworkType) {
    return [];
  }

  const isZipAnalysisReport = reportType == ReportType.ZipAnalysisAdequacyServicingProvider 
  const showTerminationProviderOption = isFilterableNetworkType(providerNetworkType) && isFilterShowableServicingProviderReportType(reportType, enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION));
  const showTargetProviderOption =  isFilterableNetworkType(providerNetworkType) && isFilterShowableServicingProviderReportType(reportType, enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING));

  switch (providerNetworkType) {
    case ProviderNetworkTypeEnum.Network:
      const networkTypeFilters =  [
        { key: NetworkProviderFilterType.Available, value: "All Providers" },
      ];

      if (showTerminationProviderOption) {
        networkTypeFilters.push({ key: NetworkProviderFilterType.Unterminated, value: "Network Minus Terminated Providers" },)
        networkTypeFilters.push({ key: NetworkProviderFilterType.Terminated, value: "Terminated Providers" })
      }        
      return networkTypeFilters;

    case ProviderNetworkTypeEnum.NetNew: {
      const netNewFilters: FormatsReturnType[] = [{ key: ExportProviderAvailabilityType.All, value: "All Providers" }];
      if (showTargetProviderOption) {
        netNewFilters.push({ key: ExportProviderAvailabilityType.Targeted, value: "Targeted Providers" });
      }
      return netNewFilters;
    }

    default:
      const defaultTypeFilters =  [
        { key: ExportProviderAvailabilityType.All, value: "All Distinct Providers" },
        { key: ExportProviderAvailabilityType.Available, value: "Distinct Available Providers" },
      ];

      if (showTargetProviderOption) {
        defaultTypeFilters.push({ key: ExportProviderAvailabilityType.Excluded, value: `${isZipAnalysisReport ? 'Distinct': ''} Excluded Providers` })
        defaultTypeFilters.push({ key: ExportProviderAvailabilityType.Targeted, value: `${isZipAnalysisReport ? 'Distinct': ''} Targeted Providers` })
      }
      return defaultTypeFilters;
  }
}

export const isFilterShowableServicingProviderReportType = (reportType: ReportType, associatedFeatureEnabled: boolean): boolean => {
  if (reportType == ReportType.ZipAnalysisAdequacyServicingProvider)
    return associatedFeatureEnabled;

  //Other case, ex. AdequacyServicingProvider case, we already have all implemented. so, no need more consideration.
  return true;
}

export const isFilterableNetworkType = (providerNetworkType: ProviderNetworkTypeEnum): boolean => {
    return  providerNetworkType !== ProviderNetworkTypeEnum.NextClosestProviders  
}

export const handleToastSuccess = (filename: string): void => {
  toast(`${filename} has been queued for generation, check email or Reports page for status.`, {
    closeOnClick: true,
    containerId: "z-index-9999",
    draggable: false,
    type: "info",
  });
}

export const handleToastError = (filename: string): void => {
  toast(`${filename} has failed, please try again or contact your system administrator.`, {
    closeOnClick: true,
    containerId: "z-index-9999",
    draggable: false,
    type: "error",
  });
}

export const exportTypeToReportTypeMap = new Map<ExportType, ReportType>([
  [ExportType.ServicingProviders, ReportType.AdequacyServicingProvider],
  [ExportType.AllSpecialties, ReportType.AdequacyAggregate],
  [ExportType.ZipSummary, ReportType.ZipSummary],
  [ExportType.ClosestProvider, ReportType.ClosestProvidersToMembers],
  [ExportType.SpecialtyAggregate, ReportType.AdequacySpecialtySummary],
  [ExportType.StateAggregate, ReportType.StateSummary],
  [ExportType.RegionAggregate, ReportType.RegionSummary],
  [ExportType.CountyAggregate, ReportType.CountySummary],
  [ExportType.SpecialtySummaryByCounty, ReportType.SpecialtySummaryByCounty],
  [ExportType.ZipAnalysisAdequacyServicingProvider, ReportType.ZipAnalysisAdequacyServicingProvider],
  [ExportType.ZipClassAggregateResultsReport, ReportType.ZipClassAggregateResultsReport],
  [ExportType.ZipAnalysisResultsReport, ReportType.ZipAnalysisResultsReport],
]);

export const reportTypeToColumnsEndpointMap = new Map<ReportType, (model?: ExportModels) => Promise<ExportColumn[]>>([
  [ReportType.AdequacyServicingProvider, exportService.getAdequacyServicingProvidersColumns],
  [ReportType.AdequacyBulkTargetSpecialtySummaryComparison, exportService.getAdequacyBulkTargetingSpecialtySummaryComparisonsColumns],
  [ReportType.AdequacyBulkTargetChangedGaps, exportService.getAdequacyBulkTargetingChangedGapsColumns],
  [ReportType.AdequacyBulkTargetProviders, () => exportService.getAdequacyBulkTargetingProvidersColumns()],
  [ReportType.AdequacyBulkTargetDistinctProviders, () => exportService.getAdequacyBulkTargetingDistinctProvidersColumns()],
  [ReportType.AdequacyBulkVolumeTargetProjectDetails, () => exportService.getAdequacyBulkTargetingProjectDetailsColumns()],
  [ReportType.AdequacyAggregate, exportService.getAdequacyAggregatesColumns],
  [ReportType.ZipSummary, exportService.getZipSummariesColumns],
  [ReportType.ClosestProvidersToMembers, exportService.getClosestProviderColumns],
  [ReportType.AdequacySpecialtySummary, exportService.getAdequacyAggregatesColumns],
  [ReportType.StateSummary, exportService.getAdequacyAggregatesColumns],
  [ReportType.RegionSummary, exportService.getAdequacyAggregatesColumns],
  [ReportType.CountySummary, exportService.getAdequacyAggregatesColumns],
  [ReportType.SpecialtySummaryByCounty, exportService.getAdequacySpecialtySummaryColumns],
  [ReportType.ZipAnalysisAdequacyServicingProvider, exportService.getAdequacyServicingProvidersColumns],
  [ReportType.ZipAnalysisResultsReport, exportService.getZipAnalysisResultsColumns],
  [ReportType.SpecialtySummaryByZipBulk, exportService.getZipAnalysisResultsColumns],
  [ReportType.ZipClassAggregateResultsReport, exportService.getZipClassAggregatesColumns],
  [ReportType.ZipClassAggregateResultsReportBulk, exportService.getZipClassAggregatesColumns],
  [ReportType.ZipAnalysisServicingProviderBulk, exportService.getAdequacyServicingProvidersColumns],
  [ReportType.ProviderHubServicingProviderReport, exportService.getProviderHubColumns],
]);